import { Alert, Card, Spin, Table, Tag, Typography } from 'antd';
import React, { useMemo } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useCustomerAdoptionStatus } from '../hooks';
import { DateRangeFilterType } from '../types';

const { Title, Text } = Typography;

interface AdoptionAnalysisTabProps {
  dateRange?: DateRangeFilterType;
  region?: number;
}

export const AdoptionAnalysisTab: React.FC<AdoptionAnalysisTabProps> = ({ dateRange = 'lastmonth', region }) => {
  // Fetch customer adoption data from API
  const { data, isLoading, error } = useCustomerAdoptionStatus(dateRange, region);

  // Transform API data to match the existing table structure
  const customerAdoption = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((item, index) => ({
      id: index,
      name: item.accountName,
      usingApp: item.usingApp.value === 'Yes',
      lastQuote: item.lastActivityDate || item.lastActivity,
      totalQuotes: item.totalQuotes,
      conversion: item.conversionRate,
    }));
  }, [data]);

  // Transform data for the adoption line chart - showing adoption rate per customer
  const adoptionOverTime = useMemo(() => {
    if (!data?.data || data.data.length === 0) return [];

    // Calculate overall adoption rate

    // Only include customers with actual quote activity, sorted by total quotes
    return [...data.data]
      .filter(item => item.totalQuotes > 0)
      .sort((a, b) => b.totalQuotes - a.totalQuotes)
      .map(item => ({
        month: item.accountName.substring(0, 10),
        conversionRate: item.conversionRate,
        totalQuotes: item.totalQuotes,
      }));
  }, [data]);
  const columns = [
    {
      title: 'Customer Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    },
    {
      title: 'Using App',
      dataIndex: 'usingApp',
      key: 'usingApp',
      sorter: (a: { usingApp: boolean }, b: { usingApp: boolean }) => Number(a.usingApp) - Number(b.usingApp),
      render: (usingApp: boolean) => (
        <Tag color={usingApp ? 'success' : 'error'}>{usingApp ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Last Quote',
      dataIndex: 'lastQuote',
      key: 'lastQuote',
      sorter: (a: { lastQuote: string }, b: { lastQuote: string }) => {
        const aTime = a.lastQuote ? new Date(a.lastQuote).getTime() : 0;
        const bTime = b.lastQuote ? new Date(b.lastQuote).getTime() : 0;
        return aTime - bTime;
      },
    },
    {
      title: 'Total Quotes',
      dataIndex: 'totalQuotes',
      key: 'totalQuotes',
      align: 'right' as const,
      sorter: (a: { totalQuotes: number }, b: { totalQuotes: number }) => a.totalQuotes - b.totalQuotes,
    },
    {
      title: 'Conversion %',
      dataIndex: 'conversion',
      key: 'conversion',
      align: 'right' as const,
      sorter: (a: { conversion: number }, b: { conversion: number }) => a.conversion - b.conversion,
      render: (conversion: number) => `${conversion}%`,
    },
    {
      title: 'Action',
      key: 'action',
      sorter: (a: { usingApp: boolean }, b: { usingApp: boolean }) => Number(a.usingApp) - Number(b.usingApp),
      render: (_: any, record: { usingApp: boolean }) =>
        !record.usingApp && <Tag color="warning">Support Visit Needed</Tag>,
    },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <Title level={4}>Customer Adoption Over Time</Title>
        <Text type="secondary" className="block mb-4">
          Percentage of customers actively using the app
        </Text>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin size="large" />
          </div>
        ) : error ? (
          <Alert
            message="Error Loading Data"
            description="Failed to load customer adoption status. Please try again."
            type="error"
            showIcon
          />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={adoptionOverTime}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                    dataKey="conversionRate"
                stroke="#EF4444"
                strokeWidth={3}
                    name="Conversion Rate %"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card>
        <Title level={4}>Detailed Customer Adoption List</Title>
        <Text type="secondary" className="block mb-4">
          Identify which customers aren't adopting - flag for support visits
        </Text>
        {error && (
          <Alert
            message="Error Loading Data"
            description="Failed to load customer adoption status. Please try again."
            type="error"
            showIcon
            className="mb-4"
          />
        )}
        <Table
          columns={columns}
          dataSource={customerAdoption}
          loading={isLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>
    </div>
  );
};
